import React from "react";
import moment from "moment";
// import "rc-slider/assets/index.css";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AvField, AvForm, AvRadio, AvRadioGroup } from "availity-reactstrap-validation";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Card, Col, DropdownItem, DropdownMenu, DropdownToggle, Label, Modal, ModalBody, ModalHeader, Nav, NavItem, NavLink, Row, TabContent, TabPane, Table, UncontrolledDropdown } from "reactstrap";
import confirm from "reactstrap-confirm";
import { CurrentUserDisplayName } from "../../api/auth";
import { siteAnnualSmsSegmentLimitSet } from "../../api/site";
import { apiSmsPackageDelete, apiSmsPackageSave, apiSmsPackagesGet } from "../../api/smsPackages";
import local from "../../localization/strings";
import Loader from "../common/Loader";
import SiteSmsExplainer from "./SiteSmsExplainer";
import SiteSmsLog from "./SiteSmsLog";

const SiteSms = ({ site }) => {
	const defaultSmsPackage = {
		id: undefined,
		segments: undefined,
		used: undefined,
		expiryDate: moment().add(1, "year").format("YYYY-MM-DD"),
		isFreePackage: false,
		notes: undefined,
	};

	const [listSize, setListSize] = useState(10);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [smsPackages, setSmsPackages] = useState(undefined);
	const [showLoading, setShowLoading] = useState(true);
	const [smsPackage, setSmsPackage] = useState(defaultSmsPackage);
	const [activeTab, setActiveTab] = useState(1);

	useEffect(() => {
		const LoadSmsPackages = async () => {
			setSmsPackages(await apiSmsPackagesGet(site.id));
			setShowLoading(false);
		};
		LoadSmsPackages();
	}, [site]);

	async function saveAnnualSmsSegmentLimit(values) {
		await siteAnnualSmsSegmentLimitSet(site.id, values.annualSmsSegmentLimit);
		toast.success(local.TS_Update_Successful);
	}

	async function saveSmsPackage(smsPackageId, values) {
		setShowLoading(true);

		try {
			await apiSmsPackageSave(site.id, smsPackageId, values.segmentsAdded, values.expiryDate, values.isFreePackage, values.locked ?? false, values.notes);
			toast.success(local.TS_Update_Successful);
		} catch (error) {
			toast.error(local.TF_SmsPackageSaveError);
		}

		// Reload packages
		setSmsPackages(await apiSmsPackagesGet(site.id));
		setModalIsOpen(false);
		setShowLoading(false);
	}

	async function deleteSmsPackage(id) {
		var result = await confirm({
			title: local.TF_SmsDeletePackage,
			message: local.TF_Delete_SmsPackage_Are_You_Sure,
			confirmColor: "danger",
			confirmText: local.TF_SmsDeletePackage,
		});

		if (result) {
			setShowLoading(true);
			try {
				await apiSmsPackageDelete(site.id, id);
				toast.success(local.TS_Update_Successful);
			} catch (error) {
				toast.error(local.TF_SmsPackageDeleteError);
			}

			// Reload packages
			setSmsPackages(await apiSmsPackagesGet(site.id));
			setModalIsOpen(false);
			setShowLoading(false);
		}
	}

	const editSmsPackage = (item) => {
		if (item.isActive) {
			setSmsPackage({
				...item,
				used: item.segmentsAdded - item.segmentsRemaining,
				expiryDate: moment(item.expiryDate).format("YYYY-MM-DD"),
			});
			setModalIsOpen(true);
		}
	};

	const clearDistributorLock = (item) => {
		saveSmsPackage(item.id, {
			locked: false,
			segmentsAdded: item.segmentsAdded,
			expiryDate: item.expiryDate,
			isFreePackage: item.isFreePackage,
			notes: item.notes,
		});
	};

	const openAddSmsPackage = () => {
		// Seed the note with who is adding it, editable before saving. The row's "Added By" column is
		// overwritten by whoever last edited the package, so it does not survive as a record of who
		// granted the segments — the note does.
		const addedBy = CurrentUserDisplayName();
		setSmsPackage({
			...defaultSmsPackage,
			notes: addedBy ? `${local.TF_SmsAddedByNote} ${addedBy}` : undefined,
		});
		setModalIsOpen(true);
	};

	const getMinimumExpiryDate = () => {
		return smsPackage.id ? moment(smsPackage.expiryDate) : moment().add(1, "day");
	};
	const getMinimumSegments = () => {
		return smsPackage.id && smsPackage.used > 0 ? smsPackage.used : 1;
	};

	// Summed from the packages already loaded rather than read off the site record, so the figure in the
	// header and the rows it is the total of cannot disagree after an add or an edit.
	const segmentsRemaining = (smsPackages || []).filter((item) => item.isActive).reduce((sum, item) => sum + (item.segmentsRemaining || 0), 0);

	return (
		<>
			{showLoading ? (
				<Loader />
			) : (
				<>
					<Modal isOpen={modalIsOpen} toggle={() => setModalIsOpen(false)} centered backdrop="static">
						<ModalHeader>
							<span className="float-left">{smsPackage.id ? local.TF_SmsEditPackage : local.TF_SmsAddPackage}</span>
							<span className="float-right close-modal" style={{ position: "absolute", top: 15, right: 20 }} onClick={() => setModalIsOpen(false)}>
								X
							</span>
						</ModalHeader>
						<ModalBody>
							<AvForm model={smsPackage} onValidSubmit={async (e, values) => await saveSmsPackage(smsPackage.id, values)}>
								<AvField name="segmentsAdded" type="number" max={2147483647} label={smsPackage.id && smsPackage.used > 0 ? `${local.TF_SmsSegments} (${smsPackage.used} ${local.TF_Used})` : local.TF_SmsSegments} validate={{ required: { value: true, errorMessage: local.TS_Required }, min: { value: getMinimumSegments(), errorMessage: `${local.TF_ValidateGreaterEqual} ${getMinimumSegments()}` }, max: { value: 2147483647 }, step: { value: 1, errorMessage: local.TF_ValidatePositiveInteger } }} />
								<AvField
									name="expiryDate"
									label={local.TF_SmsExpiryDate}
									type="date"
									min={getMinimumExpiryDate().format("YYYY-MM-DD")}
									validate={{
										required: { value: true, errorMessage: local.TS_Required },
										dateRange: {
											format: "YYYY-MM-DD",
											start: { value: getMinimumExpiryDate().format("YYYY-MM-DD") },
											end: {
												value: getMinimumExpiryDate().add(100, "year").format("YYYY-MM-DD"),
											},
											errorMessage: `${local.TF_ValidateSmsExpiry} ${getMinimumExpiryDate().format("DD MMM YYYY")}`,
										},
									}}
								/>
								<Label for="isFreePackage">{local.TF_SmsPackageType}</Label>
								<AvRadioGroup name="isFreePackage">
									<AvRadio label={local.TF_SmsPackageTypePaid} value={false} />
									<AvRadio label={local.TF_SmsPackageTypeFree} value={true} />
								</AvRadioGroup>
								{smsPackage.locked && (
									<>
										<Label for="locked">{local.TF_SmsPackageDistributorLocked}</Label>
										<AvRadioGroup name="locked">
											<AvRadio label={local.TS_No} value={false} />
											<AvRadio label={local.TS_Yes} value={true} />
										</AvRadioGroup>
									</>
								)}
								<AvField name="notes" type="textarea" rows="3" maxLength="1000" label={local.TF_Notes} />
								<Row>
									<Col>
										<Button disabled={showLoading} color="primary" className="mt-2 float-left">
											{local.TS_Save}
										</Button>
										<Button color="secondary" className="mt-2 ml-2 float-left" onClick={() => setModalIsOpen(false)}>
											{local.TS_Cancel}
										</Button>
										{smsPackage.id && smsPackage.used === 0 && (
											<Button disabled={showLoading} color="link" className="mt-2 float-right" onClick={() => deleteSmsPackage(smsPackage.id)}>
												{local.TS_Delete}
											</Button>
										)}
									</Col>
								</Row>
							</AvForm>
						</ModalBody>
					</Modal>

					{/* The dialog has no visible heading of its own, so this is the only thing naming which
					    site is being looked at — and the balance is the number it is usually opened for. */}
					<div className="d-flex justify-content-between align-items-baseline mb-2">
						<h4 className="mb-0">
							{local.TF_Sms_Information}
							<span className="fs--1 text-muted ml-2">{site.displayName}</span>
						</h4>
						<span className="fs--1 text-muted">
							{local.TF_SMS_Segments_Remaining}: <strong className="text-900">{segmentsRemaining}</strong>
						</span>
					</div>

					{/* Above the tabs: the Twilio-versus-logger-SIM distinction applies to the log as much
					    as to the balances, and is the thing most likely to be read wrong on either. */}
					<SiteSmsExplainer />

					<Nav tabs>
						<NavItem>
							<NavLink className={activeTab === 1 ? "active" : ""} onClick={() => setActiveTab(1)} style={{ cursor: "pointer" }}>
								{local.TF_SmsTabPackages}
							</NavLink>
						</NavItem>
						<NavItem>
							<NavLink className={activeTab === 2 ? "active" : ""} onClick={() => setActiveTab(2)} style={{ cursor: "pointer" }}>
								{local.TF_SmsTabLog}
							</NavLink>
						</NavItem>
					</Nav>

					<TabContent activeTab={activeTab} className="pt-3">
						<TabPane tabId={1}>
							<Card className="p-3">
								<h4>{local.TF_SmsAnnualFreeSegmentLimit}</h4>
								<AvForm onValidSubmit={async (e, values) => await saveAnnualSmsSegmentLimit(values)}>
									<span className="small">{local.TF_SmsAnnualFreeSegmentExplanation}</span>
									<AvField name="annualSmsSegmentLimit" type="number" validate={{ required: { value: true, errorMessage: local.TS_Required }, min: { value: 1, errorMessage: local.TF_ValidatePositiveInteger }, step: { value: 1, errorMessage: local.TF_ValidatePositiveInteger } }} value={site.annualSmsSegmentLimit} />
									<Row>
										<Col>
											<Button color="primary" className="mt-1">
												{local.TS_Save_Changes}
											</Button>
										</Col>
									</Row>
								</AvForm>
							</Card>
							<Card className="p-3 mt-3">
								<div className="d-flex justify-content-between w-full">
									<div>
										<h4>{local.TF_SmsPackageHistory}</h4>
									</div>
									<div>
										<Button type="button" color="primary" onClick={openAddSmsPackage}>
											{local.TF_SmsAddPackage}
										</Button>
									</div>
								</div>

								<Table className="table-hover table-sm mb-0 mt-2 fs--1 rounded-soft table-striped">
									<thead className="thead-light">
										<tr>
											<th>&nbsp;</th>
											<th>{local.TF_SmsSegmentsAdded}</th>
											<th>{local.TF_SmsSegmentsRemaining}</th>
											<th className="text-nowrap">{local.TF_SmsExpiryDate}</th>
											<th>{local.TF_SmsPackageType}</th>
											<th className="d-none d-md-table-cell text-nowrap">{local.TF_Date}</th>
											<th className="d-none d-md-table-cell text-nowrap">{local.TF_AddedBy}</th>
											<th className="d-none d-md-table-cell">{local.TF_Notes}</th>
										</tr>
									</thead>
									<tbody>
										{smsPackages &&
											smsPackages.slice(0, listSize).map((item, key) => (
												<tr key={key} className={item.isActive ? "cursor-pointer" : "table-danger hover"}>
													<td>
														<UncontrolledDropdown>
															<DropdownToggle tag="div" className="pin-button">
																<FontAwesomeIcon icon="ellipsis-v" fixedWidth />
															</DropdownToggle>
															<DropdownMenu className="border" positionFixed>
																<DropdownItem onClick={() => editSmsPackage(item)}>{local.TS_Edit}</DropdownItem>
																{smsPackage.id && smsPackage.used === 0 && <DropdownItem onClick={() => deleteSmsPackage(item.id)}>{local.TS_Delete}</DropdownItem>}
																{item.locked && <DropdownItem onClick={() => clearDistributorLock(item)}>{local.TF_SmsPackageClearDistributorLock}</DropdownItem>}
															</DropdownMenu>
														</UncontrolledDropdown>
													</td>
													<td onClick={() => editSmsPackage(item)}>{item.segmentsAdded}</td>
													<td onClick={() => editSmsPackage(item)}>{item.segmentsRemaining}</td>
													<td onClick={() => editSmsPackage(item)} className="text-nowrap">
														{moment(item.expiryDate).format("DD MMM YYYY")}
													</td>
													<td onClick={() => editSmsPackage(item)}>{item.isFreePackage ? local.TF_SmsPackageTypeFree : local.TF_SmsPackageTypePaid}</td>
													<td onClick={() => editSmsPackage(item)} className="d-none d-md-table-cell text-nowrap">
														{moment(item.dateAdded).format("DD MMM YYYY")}
													</td>
													<td onClick={() => editSmsPackage(item)} className="d-none d-md-table-cell">
														{item.addedByName}
														{item.locked && <FontAwesomeIcon className="ml-2" icon={faLock} />}
													</td>
													<td onClick={() => editSmsPackage(item)} className="d-none d-md-table-cell">
														{item.notes}
													</td>
												</tr>
											))}
									</tbody>
								</Table>
								{smsPackages.length === 0 && <div className="p-2">{local.TF_SmsPackageNone}</div>}
								{smsPackages && listSize < smsPackages.length && (
									<Button className="btn-sm mt-2" onClick={() => setListSize(listSize + 10)} color="secondary">
										{local.TF_SmsLoadMore}
									</Button>
								)}
							</Card>
						</TabPane>

						<TabPane tabId={2}>
							{/* Mounted only once its tab has been opened, so the log is not queried for
							    everyone who opens this dialog to look at the balance. */}
							{activeTab === 2 && <SiteSmsLog siteId={site.id} />}
						</TabPane>
					</TabContent>
				</>
			)}
		</>
	);
};

export default SiteSms;
